import {useCallback, useMemo, useRef, useState} from 'react'
import {useNavigate, useSearchParams} from 'react-router-dom'
import {Button, Modal, Spinner} from 'react-bootstrap'
import axios from 'axios'
import {useAuth} from '../modules/auth'

const API_URL = process.env.REACT_APP_API_URL
const POST_LOGIN_REDIRECT_KEY = 'postLoginRedirect'

type ResponseState = 'confirm' | 'loading' | 'success' | 'error'

interface InvitationResponsePayload {
  status: 'S' | 'E' | string
  text?: string
  result?: {
    appointmentId: number
    response: 'available' | 'unavailable' | string
    interpreterName?: string
  }
}

type Variant = 'available' | 'unavailable' | 'confirm' | 'error'

const VALID_RESPONSES = ['available', 'unavailable', 'confirm'] as const

const PRIMARY = '#a3a242'
const PRIMARY_SOFT = 'rgba(163, 162, 66, 0.15)'

const LINK_UNAVAILABLE_TITLE = 'The Assignment is Taken or Link Expired'
const LINK_UNAVAILABLE_MESSAGE =
  'This invitation has expired, or the assignment has been taken by another user.'
const REQUEST_FAILED_TITLE = 'Unable to Record Response'
const REQUEST_FAILED_MESSAGE =
  'We could not record your response. Please check your connection and try again, or log in to Connect to respond.'

export const InterpreterInvitationResponse = () => {
  const [searchParams] = useSearchParams()
  const navigate = useNavigate()
  const {currentUser} = useAuth()
  const [state, setState] = useState<ResponseState>('confirm')
  const [message, setMessage] = useState<string>('')
  const [errorTitle, setErrorTitle] = useState<string>(LINK_UNAVAILABLE_TITLE)
  const [responseType, setResponseType] = useState<string>('')
  const [appointmentId, setAppointmentId] = useState<number | null>(null)
  const [show, setShow] = useState(true)
  const submittedRef = useRef(false)

  const token = useMemo(() => searchParams.get('token') ?? '', [searchParams])
  const response = useMemo(() => (searchParams.get('response') ?? '').toLowerCase(), [searchParams])
  const code = useMemo(() => searchParams.get('code') ?? '', [searchParams])

  const submit = useCallback(async () => {
    if (submittedRef.current) return
    submittedRef.current = true

    if (!token || !VALID_RESPONSES.some((valid) => valid === response)) {
      setState('error')
      setMessage(LINK_UNAVAILABLE_MESSAGE)
      return
    }

    setState('loading')

    try {
      const {data} = await axios.post<InvitationResponsePayload>(
        `${API_URL}/Appoinment/interpreter-invitation-response`,
        code ? {token, response, code} : {token, response}
      )

      if (data?.status === 'S') {
        setState('success')
        setResponseType(data?.result?.response || response)
        setMessage(data?.text ?? '')
        if (data?.result?.appointmentId != null) setAppointmentId(data.result.appointmentId)
      } else {
        setState('error')
        setMessage(data?.text?.trim() || LINK_UNAVAILABLE_MESSAGE)
      }
    } catch (err: any) {
      setState('error')
      if (err?.response) {
        setMessage(LINK_UNAVAILABLE_MESSAGE)
      } else {
        setErrorTitle(REQUEST_FAILED_TITLE)
        setMessage(REQUEST_FAILED_MESSAGE)
      }
    }
  }, [token, response, code])

  const targetPath = appointmentId != null ? `/appointmentViewer/${appointmentId}` : '/dashboard'

  const goToAppointments = () => {
    navigate(targetPath, {replace: true})
  }

  const handleLogin = () => {
    try {
      localStorage.setItem(POST_LOGIN_REDIRECT_KEY, window.location.pathname + window.location.search)
    } catch {}
    navigate('/auth', {replace: true})
  }

  const handleClose = () => {
    setShow(false)
    if (currentUser) {
      navigate('/dashboard', {replace: true})
    } else {
      navigate('/auth', {replace: true})
    }
  }

  const isConfirmResponse = response === 'confirm'

  const variant: Variant =
    state === 'success'
      ? isConfirmResponse
        ? 'confirm'
        : responseType === 'unavailable'
        ? 'unavailable'
        : 'available'
      : 'error'

  const isAvailableResponse = response === 'available'
  const isLoggedIn = !!currentUser

  const renderIcon = () => {
    if (state === 'loading' || state === 'confirm') return null

    if (variant === 'available' || variant === 'confirm') {
      return (
        <svg width='36' height='36' viewBox='0 0 24 24' fill='none'>
          <circle cx='12' cy='12' r='10' stroke={PRIMARY} strokeWidth='2' />
          <path
            d='M7.5 12.5l3 3 6-6'
            stroke={PRIMARY}
            strokeWidth='2.2'
            strokeLinecap='round'
            strokeLinejoin='round'
          />
        </svg>
      )
    }

    return (
      <svg width='36' height='36' viewBox='0 0 24 24' fill='none'>
        <circle cx='12' cy='12' r='10' stroke={PRIMARY} strokeWidth='2' />
        <path d='M12 7.5v5.5' stroke={PRIMARY} strokeWidth='2.2' strokeLinecap='round' />
        <circle cx='12' cy='16.25' r='1.15' fill={PRIMARY} />
      </svg>
    )
  }

  const renderContent = () => {
    if (state === 'loading') {
      return (
        <div className='d-flex flex-column align-items-center py-8'>
          <Spinner animation='border' style={{color: PRIMARY}} />
          <p className='mt-4 mb-0 text-gray-600'>Recording your response, please wait...</p>
        </div>
      )
    }

    if (state === 'confirm') {
      if (!isLoggedIn) {
        return (
          <>
            <h3 className='fw-bolder fs-2 text-gray-900 mb-4'>Login Required</h3>
            <p className='fs-5 text-gray-800 mb-3'>
              Please log in to{' '}
              {isConfirmResponse
                ? 'confirm this appointment'
                : `confirm your ${isAvailableResponse ? 'availability' : 'unavailability'} for this appointment`}
              .
            </p>
          </>
        )
      }

      return (
        <>
          <h3 className='fw-bolder fs-2 text-gray-900 mb-4'>
            {isConfirmResponse
              ? 'Confirm Your Assignment'
              : isAvailableResponse
              ? 'Confirm Your Availability'
              : 'Confirm Your Unavailability'}
          </h3>
          <p className='fs-5 text-gray-800 mb-3'>
            {isConfirmResponse
              ? 'You are about to confirm that you will take this appointment.'
              : isAvailableResponse
              ? 'You are about to mark yourself as available for this appointment.'
              : 'You are about to mark yourself as unavailable for this appointment.'}
          </p>
          <p className='fs-6 text-gray-500 mb-0'>
            Please click the button below to confirm.
          </p>
        </>
      )
    }

    if (variant === 'confirm') {
      return (
        <>
          <h3 className='fw-bolder fs-2 text-gray-900 mb-4'>Assignment Confirmed</h3>
          <p className='fs-5 text-gray-800 mb-3'>
            Thank you for confirming. We have let the team know you will be attending.
          </p>
          <p className='fs-6 text-gray-500 mb-0'>
            You can review the appointment details at any time in Connect.
          </p>
        </>
      )
    }

    if (variant === 'available') {
      return (
        <>
          <h3 className='fw-bolder fs-2 text-gray-900 mb-4'>Availability Confirmed</h3>
          <p className='fs-5 text-gray-800 mb-3'>
            You have marked yourself available for the appointment.
          </p>
          <p className='fs-6 text-gray-500 mb-0'>
            If assigned, you will receive a confirmation email.
          </p>
        </>
      )
    }

    if (variant === 'unavailable') {
      return (
        <>
          <h3 className='fw-bolder fs-2 text-gray-900 mb-4 px-4'>
            You have marked yourself unavailable for the appointment.
          </h3>
          <p className='fs-6 text-gray-500 mb-0 px-4'>
            You can review other appointments that matches your availability
          </p>
        </>
      )
    }

    return (
      <>
        <h3 className='fw-bolder fs-2 text-gray-900 mb-4'>{errorTitle}</h3>
        <p className='fs-6 text-gray-600 mb-0 px-4'>{message}</p>
      </>
    )
  }

  return (
    <div
      style={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: '#f5f8fa',
        padding: '20px',
      }}
    >
      <Modal
        show={show}
        onHide={handleClose}
        backdrop='static'
        keyboard={false}
        centered
        contentClassName='border-0 shadow-lg'
        dialogClassName='interpreter-invitation-modal'
      >
        <Modal.Body className='text-center px-10 pt-8 pb-8 position-relative'>
          <button
            type='button'
            aria-label='Close'
            onClick={handleClose}
            className='btn btn-icon btn-sm position-absolute'
            style={{top: 16, right: 16, color: '#6c7293'}}
          >
            <i className='bi bi-x-lg fs-4' />
          </button>

          {state !== 'loading' && state !== 'confirm' && (
            <div
              className='d-inline-flex align-items-center justify-content-center mb-6'
              style={{
                width: 72,
                height: 72,
                borderRadius: '50%',
                background: PRIMARY_SOFT,
              }}
            >
              {renderIcon()}
            </div>
          )}

          {renderContent()}

          {state === 'confirm' && !isLoggedIn && (
            <div className='d-flex justify-content-center gap-3 mt-8'>
              <Button
                variant='light'
                onClick={handleClose}
                className='px-6 py-3 fw-semibold'
                style={{
                  minWidth: 140,
                  background: '#ffffff',
                  border: '1px solid #e4e6ef',
                  color: '#181c32',
                }}
              >
                Cancel
              </Button>
              <Button
                onClick={handleLogin}
                className='px-6 py-3 fw-semibold text-white border-0'
                style={{
                  minWidth: 180,
                  background: PRIMARY,
                }}
              >
                Log In
              </Button>
            </div>
          )}

          {state === 'confirm' && isLoggedIn && (
            <div className='d-flex justify-content-center gap-3 mt-8'>
              <Button
                variant='light'
                onClick={handleClose}
                className='px-6 py-3 fw-semibold'
                style={{
                  minWidth: 140,
                  background: '#ffffff',
                  border: '1px solid #e4e6ef',
                  color: '#181c32',
                }}
              >
                Cancel
              </Button>
              <Button
                onClick={submit}
                className='px-6 py-3 fw-semibold text-white border-0'
                style={{
                  minWidth: 180,
                  background: isAvailableResponse || isConfirmResponse ? PRIMARY : '#e4575f',
                }}
              >
                {isConfirmResponse
                  ? 'Confirm Assignment'
                  : isAvailableResponse
                  ? 'Confirm Available'
                  : 'Confirm Unavailable'}
              </Button>
            </div>
          )}

          {state !== 'loading' && state !== 'confirm' && (
            <div className='d-flex justify-content-center gap-3 mt-8'>
              <Button
                variant='light'
                onClick={handleClose}
                className='px-6 py-3 fw-semibold'
                style={{
                  minWidth: 140,
                  background: '#ffffff',
                  border: '1px solid #e4e6ef',
                  color: '#181c32',
                }}
              >
                Close
              </Button>
              <Button
                onClick={goToAppointments}
                className='px-6 py-3 fw-semibold text-white border-0'
                style={{
                  minWidth: 180,
                  background: PRIMARY,
                }}
              >
                View Appointments
              </Button>
            </div>
          )}
        </Modal.Body>
      </Modal>
    </div>
  )
}

export default InterpreterInvitationResponse
